import { apiFetch } from '@/lib/api/client';
import type {
  InviteOrganizationAdminInput,
  InviteStudentInput,
  InviteTeacherInput,
  InviteToOrganizationResult,
  OrganizationInvitationView,
} from '@/features/membership/types';

/**
 * `POST /teacher-invitations` (ADR-027, Decision 4). Tenant-scoped
 * (`tenantScoped: true`) - `X-Organization-Id` is attached automatically
 * from the current Active Organization.
 */
export async function inviteTeacher(
  input: InviteTeacherInput,
): Promise<InviteToOrganizationResult> {
  return apiFetch<InviteToOrganizationResult>('/teacher-invitations', {
    method: 'POST',
    body: input,
    tenantScoped: true,
  });
}

/** `POST /student-invitations` (ADR-027, Decision 5). Tenant-scoped. */
export async function inviteStudent(
  input: InviteStudentInput,
): Promise<InviteToOrganizationResult> {
  return apiFetch<InviteToOrganizationResult>('/student-invitations', {
    method: 'POST',
    body: input,
    tenantScoped: true,
  });
}

/**
 * `POST /organization-admin-invitations` (ADR-027, Decision 6). NOT
 * tenant-scoped - the inviting Platform Super Admin holds no tenant
 * membership, so the target organization is named explicitly in the body
 * rather than resolved from an Active Organization.
 */
export async function inviteOrganizationAdmin(
  input: InviteOrganizationAdminInput,
): Promise<InviteToOrganizationResult> {
  return apiFetch<InviteToOrganizationResult>('/organization-admin-invitations', {
    method: 'POST',
    body: input,
  });
}

/** `GET /organization-invitations`. Tenant-scoped. */
export async function listOrganizationInvitations(): Promise<
  readonly OrganizationInvitationView[]
> {
  return apiFetch<readonly OrganizationInvitationView[]>('/organization-invitations', {
    tenantScoped: true,
  });
}

/** `POST /organization-invitations/:id/resend` (ADR-027, Decision 9). Tenant-scoped. */
export async function resendOrganizationInvitation(
  id: string,
): Promise<InviteToOrganizationResult> {
  return apiFetch<InviteToOrganizationResult>(`/organization-invitations/${id}/resend`, {
    method: 'POST',
    tenantScoped: true,
  });
}

/** `POST /organization-invitations/:id/cancel` (ADR-027, Decision 10). Tenant-scoped. */
export async function cancelOrganizationInvitation(
  id: string,
): Promise<OrganizationInvitationView> {
  return apiFetch<OrganizationInvitationView>(`/organization-invitations/${id}/cancel`, {
    method: 'POST',
    tenantScoped: true,
  });
}

/**
 * `POST /organization-invitations/:token/accept`. Deliberately takes no
 * organization id and is NOT `tenantScoped` - the backend derives the
 * organization entirely from the token's linked invitation row (ADR-027,
 * Decision 13), so this must keep working with no Active Organization
 * selected, mirroring `acceptGuardianInvitation` exactly.
 */
export async function acceptOrganizationInvitation(
  token: string,
): Promise<OrganizationInvitationView> {
  return apiFetch<OrganizationInvitationView>(`/organization-invitations/${token}/accept`, {
    method: 'POST',
  });
}
